import { useState, useCallback, useRef } from 'react';
import { heidiApiService } from '../../services/heidiApi';

export interface HeidiTranscriptionState {
  isRecording: boolean;
  isProcessing: boolean;
  transcript: any | null;
  liveTranscript: string | null;
  consultNote: any | null;
  error: string | null;
}

export const useHeidiTranscription = () => {
  const [state, setState] = useState<HeidiTranscriptionState>({
    isRecording: false,
    isProcessing: false,
    transcript: null,
    liveTranscript: null,
    consultNote: null,
    error: null,
  });

  // ScriptProcessor refs (EXACT HTML METHOD)
  const audioContextRef = useRef<any>(null);
  const sourceRef = useRef<any>(null);
  const processorRef = useRef<any>(null);
  const audioChunksRef = useRef<Float32Array[]>([]);
  const liveTranscriptIntervalRef = useRef<any>(null);
  const sendIntervalRef = useRef<any>(null);

  // AudioBuffer to WAV encoder (EXACT HTML METHOD)
  const audioBufferToWav = (buffer: AudioBuffer): Blob => {
    const numOfChan = buffer.numberOfChannels;
    const length = buffer.length * numOfChan * 2 + 44;
    const bufferArray = new ArrayBuffer(length);
    const view = new DataView(bufferArray);

    const writeString = (offset: number, s: string) => {
      for (let i = 0; i < s.length; i++) {
        view.setUint8(offset + i, s.charCodeAt(i));
      }
    };

    // RIFF header
    writeString(0, 'RIFF');
    view.setUint32(4, length - 8, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // Subchunk1Size
    view.setUint16(20, 1, true); // AudioFormat (PCM)
    view.setUint16(22, numOfChan, true);
    view.setUint32(24, buffer.sampleRate, true);
    view.setUint32(28, buffer.sampleRate * numOfChan * 2, true); // ByteRate
    view.setUint16(32, numOfChan * 2, true); // BlockAlign
    view.setUint16(34, 16, true); // BitsPerSample
    writeString(36, 'data');
    view.setUint32(40, buffer.length * numOfChan * 2, true);

    // Write PCM samples
    let offset = 44;
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numOfChan; channel++) {
        const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
        offset += 2;
      }
    }

    return new Blob([bufferArray], { type: 'audio/wav' });
  };

  // Send WAV chunk (EXACT HTML METHOD)
  const sendWavChunk = async () => {
    if (audioChunksRef.current.length === 0 || !audioContextRef.current) return;

    const current = [...audioChunksRef.current];
    audioChunksRef.current = [];

    const totalLength = current.reduce((sum, chunk) => sum + chunk.length, 0);

    // FIX: Use the hardware's actual sample rate
    const sampleRate = audioContextRef.current.sampleRate;
    const buffer = audioContextRef.current.createBuffer(1, totalLength, sampleRate);

    let offset = 0;
    current.forEach(chunk => {
      buffer.getChannelData(0).set(chunk, offset);
      offset += chunk.length;
    });

    const wavBlob = audioBufferToWav(buffer);

    try {
      await heidiApiService.uploadAudioChunk(wavBlob);
    } catch (error) {
      console.error('❌ Upload failed:', error);
    }
  };

  const startRecording = useCallback(async (email: string, userId: string) => {
    try {
      setState(prev => ({ ...prev, isProcessing: true, error: null }));

      // Get JWT
      await heidiApiService.getJWT(email, userId);

      // Create session
      await heidiApiService.createSession();

      // Initialize transcription
      await heidiApiService.initializeTranscription();

      // Replace this block in startRecording
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // FIX: Remove the { sampleRate: 16000 } constraint
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();

      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      processorRef.current = audioContextRef.current.createScriptProcessor(4096, 1, 1);

      // Collect audio samples (EXACT HTML METHOD)
      processorRef.current.onaudioprocess = (e: any) => {
        audioChunksRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };

      sourceRef.current.connect(processorRef.current);
      processorRef.current.connect(audioContextRef.current.destination);

      // Send WAV chunks every 5 seconds (EXACT HTML METHOD)
      sendIntervalRef.current = setInterval(sendWavChunk, 5000);

      // Start polling live transcript every 2 seconds
      liveTranscriptIntervalRef.current = setInterval(async () => {
        try {
          const liveData = await heidiApiService.getLiveTranscript();
          const chunks = liveData?.response?.live?.[0]?.chunks || [];
          const liveText = chunks
            .filter((c: any) => c.transcript)
            .map((c: any) => c.transcript)
            .join(' ');
          if (liveText) {
            setState(prev => ({ ...prev, liveTranscript: liveText }));
          }
        } catch (error) {
          console.error('Error fetching live transcript:', error);
        }
      }, 2000);

      setState(prev => ({
        ...prev,
        isRecording: true,
        isProcessing: false,
        error: null,
      }));
    } catch (error) {
      console.error('Error starting recording:', error);
      setState(prev => ({
        ...prev,
        isProcessing: false,
        error: error instanceof Error ? error.message : 'Failed to start recording',
      }));
    }
  }, []);

  const stopRecording = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isProcessing: true }));

      // Stop intervals
      if (liveTranscriptIntervalRef.current) {
        clearInterval(liveTranscriptIntervalRef.current);
      }
      if (sendIntervalRef.current) {
        clearInterval(sendIntervalRef.current);
      }

      // Send final chunk (EXACT HTML METHOD)
      await sendWavChunk();

      // Disconnect audio (EXACT HTML METHOD)
      if (processorRef.current) {
        processorRef.current.disconnect();
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        await audioContextRef.current.close();
      }

      // Finish transcription
      await heidiApiService.finishTranscription();

      // Trigger consult note generation (streaming - we don't wait for it)
      console.log('📝 Triggering consult note generation...');
      await heidiApiService.generateConsultNote(
        '6585134b16a2bab5da68a919', // template_id from HTML
        'TEMPLATE',
        'GOLDILOCKS',
        'LEFT'
      );

      // Poll session status until consult_note.status === COMPLETED
      console.log('⏳ Waiting for consult note to complete...');
      let ready = false;
      let count = 0;
      while (!ready && count < 50) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const sessionData = await heidiApiService.getSessionStatus();
        console.log(`⏳ Poll ${count + 1}/50: status = ${sessionData?.session?.consult_note?.status || 'PENDING'}`);

        if (sessionData?.session?.consult_note?.status === 'COMPLETED') {
          ready = true;
          console.log('✅ Consult note generation completed!');
        }
        count++;
      }

      if (!ready) {
        console.warn('⚠️ Consult note generation timed out after 100s');
      }

      // Get final formatted note using client-customised-template/response
      console.log('📝 Fetching formatted clinical note...');
      const consultNote = await heidiApiService.getCustomNotes('DOCUMENT_TEMPLATE', [], true, [
        '(only include info explicitly mentioned)',
        '(Never hallucinate patient details)',
      ]);

      console.log('📝 Final formatted note received:', {
        hasContent: !!consultNote?.content,
        hasSummary: !!consultNote?.summary,
        hasQuestions: !!consultNote?.questionAnswers,
      });

      setState(prev => ({
        ...prev,
        isRecording: false,
        isProcessing: false,
        consultNote,
        error: null,
      }));

      return { consultNote, liveTranscript: state.liveTranscript };
    } catch (error) {
      console.error('Error stopping recording:', error);
      setState(prev => ({
        ...prev,
        isRecording: false,
        isProcessing: false,
        liveTranscript: null,
        error: error instanceof Error ? error.message : 'Failed to stop recording',
      }));
      throw error;
    }
  }, []);

  const generateNote = useCallback(async (templateId: string) => {
    try {
      setState(prev => ({ ...prev, isProcessing: true }));

      const consultNote = await heidiApiService.generateConsultNote(templateId);

      setState(prev => ({
        ...prev,
        consultNote,
        isProcessing: false,
        error: null,
      }));

      return consultNote;
    } catch (error) {
      console.error('Error generating note:', error);
      setState(prev => ({
        ...prev,
        isProcessing: false,
        error: error instanceof Error ? error.message : 'Failed to generate note',
      }));
      throw error;
    }
  }, []);

  const getTemplates = useCallback(async () => {
    try {
      const templates = await heidiApiService.getTemplates();
      return templates;
    } catch (error) {
      console.error('Error getting templates:', error);
      throw error;
    }
  }, []);

  const reset = useCallback(() => {
    // Clear intervals
    if (liveTranscriptIntervalRef.current) {
      clearInterval(liveTranscriptIntervalRef.current);
    }
    if (sendIntervalRef.current) {
      clearInterval(sendIntervalRef.current);
    }

    // Disconnect audio (EXACT HTML METHOD)
    if (processorRef.current) {
      processorRef.current.disconnect();
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }

    heidiApiService.resetSession();
    audioChunksRef.current = [];

    setState({
      isRecording: false,
      isProcessing: false,
      transcript: null,
      liveTranscript: null,
      consultNote: null,
      error: null,
    });
  }, []);

  return {
    ...state,
    startRecording,
    stopRecording,
    generateNote,
    getTemplates,
    reset,
  };
};
